import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { ActivityTrackerService } from './activity-tracker.service';

@Injectable()
export class HttpLoggingInterceptor implements HttpInterceptor {

  constructor(private activityTrackerService: ActivityTrackerService) {}
  private MAX_LOG_LENGTH = 500;

  private static readonly REDACTED_HEADERS = new Set(['authorization', 'cookie', 'set-cookie']);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const started = Date.now();

    if (req.url.startsWith('https://www.googleapis.com')) {
      return next.handle(req);
    }
    // Log request details. Cookies and the Authorization header are never logged, even trimmed.
    this.activityTrackerService.addLog(`HTTP Request: ${JSON.stringify({
      url: req.urlWithParams,
      method: req.method,
      headers: this.trimObject(req.headers.keys().reduce((acc: { [key: string]: any }, key: string) => {
        acc[key] = req.headers.getAll(key) || [];
        return acc;
      }, {} as { [key: string]: any })),
      cookies: '[REDACTED]',
      body: this.trimString(this.safeStringify(req.body)),
      timestamp: new Date(started).toISOString()
    })}`);

    return next.handle(req).pipe(
      tap({
        next: event => {
          if (event instanceof HttpResponse) {
            const ended = Date.now();
            const duration = ended - started;

            // Log response details. Cookies are never logged, even trimmed.
            this.activityTrackerService.addLog(`HTTP Response: ${JSON.stringify({
              url: event.url,
              status: event.status,
              statusText: event.statusText,
              headers: this.trimObject(event.headers.keys().reduce((acc: { [key: string]: any }, key: string) => {
                acc[key] = event.headers.getAll(key) || [];
                return acc;
              }, {})),
              cookies: '[REDACTED]',
              body: this.trimString(this.safeStringify(event.body)),
              timestamp: new Date(ended).toISOString(),
              durationMs: duration
            })}`);
          }
        },
        error: (error: HttpErrorResponse) => {
          const ended = Date.now();
          const duration = ended - started;
          this.activityTrackerService.addLog(`HTTP Error: ${JSON.stringify({
            url: req.urlWithParams,
            status: error.status,
            message: error.message,
            error: this.trimString(this.safeStringify(error.error)),
            timestamp: new Date(ended).toISOString(),
            durationMs: duration
          })}`);
        }
      })
    );
  }

  private trimString(input: string): string {
    if (!input) return '';
    return input.length > this.MAX_LOG_LENGTH ? input.slice(0, this.MAX_LOG_LENGTH) + '...[truncated]' : input;
  }

  private trimObject(obj: any): any {
    if (!obj) return obj;
    const trimmedObj: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (HttpLoggingInterceptor.REDACTED_HEADERS.has(key.toLowerCase())) {
          trimmedObj[key] = '[REDACTED]';
          continue;
        }
        const value = Array.isArray(obj[key]) ? obj[key].join(', ') : obj[key];
        trimmedObj[key] = this.trimString(value);
      }
    }
    return trimmedObj;
  }

  private safeStringify(data: any): string {
    try {
      return JSON.stringify(data);
    } catch {
      return String(data);
    }
  }
}
